import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { Button } from '@streamr/streamr-layout'
import { TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import downloadFile from './assets/download-file.png'
import downloadFile2x from './assets/download-file@2x.png'
import Topic from '~shared/Topic'
import Container from '~shared/Container'
import Display from '~shared/Display'
import Br from '~shared/Br'
import Spacer from '~shared/Spacer'
import PrestyledFeatureList from '~shared/FeatureList'
import {
    SCALABILITY,
    DECENTRALIZATION,
    LATENCY,
    PAYLOAD,
    BANDWIDTH,
    COMPLETENESS,
    TOLERANCE,
    RESILIENCE,
    SIMPLICITY,
} from '~shared/FeatureGlyph'
import urls from '~utils/urls'

const FeatureList = styled(PrestyledFeatureList)`
    ${PrestyledFeatureList.Item} + ${PrestyledFeatureList.Item} {
        margin-top: 1.5rem;
    }
`

const FeatureListItem = styled(FeatureList.Item)`
    font-size: 0.875rem;
    line-height: 1.375rem;

    > div {
        flex: 1;
    }

    dd {
        color: #525252;
    }

    ${({ theme }) => !theme.breakLine && css`
        dt {
            margin-right: 0.5rem;
            float: left;
        }
    `}

    @media (min-width: ${TABLET}px) {
        font-size: 1rem;
        line-height: 1.875rem;
    }
`

const UnstyledWhitepaper = forwardRef((props, ref) => (
    <div {...props} ref={ref}>
        <img src={downloadFile} srcSet={downloadFile2x} alt="" />
        <br />
        <Button
            tag="a"
            kind="primary"
            size="big"
            href={urls.documents.networkWhitepaper}
        >
            Get Network Whitepaper
        </Button>
    </div>
))

const Whitepaper = styled(UnstyledWhitepaper)`
    background-color: #F5F5F5;
    border-radius: 8px;
    padding: 3rem 2rem 2.5rem;
    text-align: center;

    img {
        width: 92px;
        margin-bottom: 1.5rem;
        margin-right: 1.5rem;
    }

    @media (max-width: ${TABLET - 1}px) {
        img {
            margin-bottom: 2.25rem;
        }

        ${Button} {
            width: 100%;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        img {
            width: 148px;
            margin-bottom: 3rem;
            margin-right: 2.5rem;
        }

        ${Button} {
            width: 100%;
            padding: 0;
        }
    }
`

const Header = styled.div`
    ${Topic.Title} {
        margin: 0;
        text-align: left;
        margin-bottom: 2rem;
        white-space: nowrap;
    }

    ${Whitepaper} {
        display: none;
    }

    @media (min-width: ${DESKTOP}px) {
        ${Topic.Title} {
            margin-bottom: 1.5rem;
        }

        ${Whitepaper} {
            display: block;
            margin-top: 6.5rem;
        }
    }
`

const Principles = styled.div`
    ${FeatureListItem} {
        background-color: #F5F5F5;
        padding: 1.75rem 1rem 1.75rem 2rem;
        border-radius: 8px;
        align-items: flex-start;

        img {
            width: 48px;
            height: 48px;
            margin-right: 1.75rem;
        }
    }

    @media (min-width: ${TABLET}px) {
        ${FeatureListItem} {
            padding: 1.875rem 1rem 1.875rem 2.35rem;
            align-items: center;

            img {
                margin-right: 2rem;
            }
        }
    }

    @media (min-width: ${DESKTOP}px) {
        ${FeatureListItem} {
            padding: 1.875rem 2rem;
        }
    }
`

const Inner = styled.div`
    ${Principles} {
        margin-top: 3rem;
    }

    @media (min-width: ${TABLET}px) {
        ${Principles} {
            margin-top: 4.5rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        ${Header} {
            flex: 1;
            max-width: 296px;
        }

        ${Principles} {
            margin-top: 0;
            flex: 1;
            max-width: 50%;
        }
    }
`

const DesignPrinciples = () => (
    <Topic pad head="7.5rem,,17rem" tail="5rem,6rem,16rem" backgroundColor="#FAFAFA" color="#0C009A">
        <Container>
            <Inner>
                <Header>
                    <Topic.Title $appear>
                        Design principles
                    </Topic.Title>
                    <Topic.Paragraph $appear>
                        Explore some of the design principles that guide network development or grab the whitepaper for more details.
                    </Topic.Paragraph>
                    <div $appear>
                        <Display
                            as={Whitepaper}
                            mobile="none"
                            desktop="block"
                        />
                    </div>
                </Header>
                <Principles>
                    <FeatureList>
                        <FeatureListItem
                            $appear
                            title="Scalable"
                            glyph={SCALABILITY}
                        >
                            Provide acceptable quality of service
                            <Br tablet />
                            no matter how many nodes are added
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Decentralized"
                            glyph={DECENTRALIZATION}
                            theme={{
                                breakLine: true,
                            }}
                        >
                            Avoid hotspots or central points of failure
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Low and predictable latency"
                            glyph={LATENCY}
                        >
                            Subscribers should
                            <Br tablet />
                            receive each event without unnecessary delays
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Optimized for small payloads per message"
                            glyph={PAYLOAD}
                            theme={{
                                breakLine: true,
                            }}
                        >
                            Works best with large numbers of smaller messages
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Efficient"
                            glyph={BANDWIDTH}
                        >
                            The number of unnecessary messages
                            <Br tablet />
                            transmitted in the network should be low
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Message complete"
                            glyph={COMPLETENESS}
                        >
                            Nodes should receive all the
                            <Br tablet />
                            messages of subscribed streams with high probability
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Churn tolerant"
                            glyph={TOLERANCE}
                        >
                            Offer good quality of service even
                            <Br tablet />
                            if nodes join and leave the network at a rapid pace
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Resilient"
                            glyph={RESILIENCE}
                            theme={{
                                breakLine: true,
                            }}
                        >
                            Should provide resilience to all known attacks
                        </FeatureListItem>
                        <FeatureListItem
                            $appear
                            title="Simple"
                            glyph={SIMPLICITY}
                        >
                            Should be reliable in real-life use,
                            <Br tablet />
                            simple and easy to debug
                        </FeatureListItem>
                    </FeatureList>
                </Principles>
                <Display desktop="none">
                    <Spacer head="1.5rem">
                        <Whitepaper $appear />
                    </Spacer>
                </Display>
            </Inner>
        </Container>
    </Topic>
)

export default DesignPrinciples
